import { useEffect, useRef, type RefObject } from "react";

const DEFAULT_DEEP_LINK_OBSERVER_TIMEOUT_MS = 10_000;

type UseScrollToDeepLinkTargetOptions = {
  rootRef: RefObject<HTMLElement | null>;
  targetId: string | undefined;
  enabled?: boolean;
  onFound?: () => void;
  behavior?: ScrollBehavior;
  timeoutMs?: number;
};

/**
 * Scrolls to a target element once it is available in a scroll container.
 *
 * The hook first checks for an already-mounted target, then attaches a
 * MutationObserver to wait for async/conditional DOM insertion such as expanded
 * rows or lazy-rendered content. The observer disconnects after a successful
 * scroll, when the effect cleans up, or after the timeout elapses.
 */
export function useScrollToDeepLinkTarget({
  rootRef,
  targetId,
  enabled = true,
  onFound,
  behavior = "smooth",
  timeoutMs = DEFAULT_DEEP_LINK_OBSERVER_TIMEOUT_MS,
}: UseScrollToDeepLinkTargetOptions) {
  const lastScrolledKeyRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!targetId) {
      lastScrolledKeyRef.current = undefined;
      return;
    }

    if (!enabled) {
      return;
    }

    if (lastScrolledKeyRef.current === targetId) {
      return;
    }

    const root = rootRef.current;
    if (!root) {
      return;
    }

    const findElement = () => {
      const element = root.ownerDocument.getElementById(targetId);
      return element instanceof HTMLElement && root.contains(element) ? element : undefined;
    };

    const scrollToElement = (element: HTMLElement) => {
      lastScrolledKeyRef.current = targetId;
      onFound?.();
      element.scrollIntoView({ block: "start", inline: "nearest", behavior });
      element.focus({ preventScroll: true });
    };

    const existingElement = findElement();
    if (existingElement) {
      scrollToElement(existingElement);
      return;
    }

    if (typeof MutationObserver === "undefined") {
      return;
    }

    let timeout: ReturnType<typeof setTimeout> | undefined;
    const observer = new MutationObserver(() => {
      const element = findElement();
      if (!element) {
        return;
      }

      observer.disconnect();
      if (timeout !== undefined) {
        clearTimeout(timeout);
        timeout = undefined;
      }
      scrollToElement(element);
    });

    observer.observe(root, {
      childList: true,
      subtree: true,
    });

    timeout = setTimeout(() => {
      observer.disconnect();
      timeout = undefined;
    }, timeoutMs);

    return () => {
      observer.disconnect();
      if (timeout !== undefined) {
        clearTimeout(timeout);
      }
    };
  }, [behavior, enabled, onFound, rootRef, targetId, timeoutMs]);
}
